<template>
  <component
    :is="url ? UiSmartLink : 'button'"
    :to="url"
    :class="['btn-container', type, { 'button-link': url }]"
    :rel="isNewTab ? 'noopener noreferrer' : null"
    :target="isNewTab ? '_blank' : null"
    :disabled="isDisabled"
    :aria-label="text"
    @click="onClick"
  >
    <span class="btn-content">
      <UiIcon
        v-if="icons?.left?.name"
        :name="icons.left.name"
        :color-fill="icons.left.colorFill"
        :size="icons.left.size"
      />
      <span v-if="text" class="text">
        {{ text }}
      </span>
      <UiIcon
        v-if="icons?.right?.name"
        :name="icons.right.name"
        :color-fill="icons.right.colorFill"
        :size="icons.right.size"
      />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'
import UiSmartLink from '@sephora-asia/ui-smart-link/UiSmartLink.vue'
import type { PropsIcon } from './types'
import { BtnType, BtnSize } from './types'

interface Props {
  icons?: PropsIcon
  isDisabled?: boolean
  isNewTab?: boolean
  size?: BtnSize
  text?: string
  type?: BtnType
  url?: string | object
}

const props = withDefaults(defineProps<Props>(), {
  icons: () => {
    return {
      left: {
        name: '',
        colorFill: '',
        size: '',
      },
      right: {
        name: '',
        colorFill: '',
        size: '',
      },
    }
  },
  isDisabled: false,
  isNewTab: false,
  size: BtnSize.SMALL,
  text: '',
  type: BtnType.PRIMARY,
  url: '',
})

const emit = defineEmits(['clicked'])

function onClick(): void {
  if (props.isDisabled) return

  emit('clicked')
}

const resolveBtnSize = computed((): string => {
  return props.size === BtnSize.LARGE ? '48px' : '40px'
})
</script>

<style lang="scss" scoped>
@mixin button-variant($color, $background, $hoverColor) {
  background-color: $background;
  color: $color;

  &:is(:hover, :active, :focus) {
    background-color: $hoverColor;
    box-shadow: none;
    color: $color;
  }
}

.btn-container {
  border: none;
  border-radius: var(--size-radius-medium);
  cursor: pointer;
  padding: 0;

  &:not(:disabled) * {
    cursor: pointer;
  }

  &.button-link {
    text-decoration: none;
    display: inline-block;
  }
}

.btn-content {
  @extend .headline-m;

  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 24px;
  padding-right: 24px;
  gap: 8px;
  min-height: v-bind('resolveBtnSize');
}

button:disabled {
  pointer-events: none;
  border: none;
  @include button-variant(
    var(--color-base-grey-30),
    var(--color-base-grey-10),
    var(--color-base-grey-10)
  );
}

.primary {
  @include button-variant(
    var(--color-font-button-primary),
    var(--color-base-red-100),
    var(--color-base-red-70)
  );
}

.secondary {
  @include button-variant(
    var(--color-font-button-secondary),
    var(--color-base-black),
    var(--color-base-grey-70)
  );
}

.tertiary {
  border: 1px solid var(--color-base-black);
  line-height: calc(
    v-bind('resolveBtnSize') - 2px
  ); //Adjusting button height as per border size

  @include button-variant(
    var(--color-font-button-tertiary),
    var(--color-base-white),
    var(--color-base-white)
  );
  &:is(:hover, :active, :focus) {
    color: var(--color-base-grey-70);
    border: 1px solid var(--color-base-grey-70);
  }
}

.transparent {
  background: transparent;
}
</style>
